import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useToast } from '../contexts/ToastContext'

/**
 * "When did my store last sync?" — answers the most common support ticket, which is usually a
 * sync that worked but had not reached that product yet. Data comes from get_merchant_sync_status
 * (membership-checked); the connection check and token replacement from merchant-store-connection.
 */

interface SyncStatus {
  platform: 'shopify' | 'woocommerce'
  sync_enabled: boolean
  has_location: boolean
  subscription_status: string | null
  subscription_ends_at: string | null
  last_completed_at: string | null
  last_completed_products: number | null
  running: { started_at: string; batch: number | null; total_batches: number | null } | null
  last_failure: { at: string; error: string | null } | null
  typical_interval_minutes: number | null
  // 'shopify_app': installed through the FlowStock Shopify app, so there is no token to paste
  connection?: 'shopify_app' | 'token'
}

interface Connection {
  ok: boolean
  store?: string
  reason?: string
  missing_scopes?: { scope: string; why: string }[]
  saved?: boolean
}

export function timeAgo(iso: string, now = Date.now()): string {
  const mins = Math.round((now - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.round(mins / 60)
  if (hours < 48) return `${hours} h ago`
  return `${Math.round(hours / 24)} days ago`
}

async function invokeConnection(body: Record<string, unknown>): Promise<Connection> {
  const { data, error } = await supabase.functions.invoke('merchant-store-connection', { body })
  if (error) {
    // 422 = the new credentials were tested and rejected; the body says why.
    const ctx: any = (error as any).context
    const payload = typeof ctx?.json === 'function' ? await ctx.json().catch(() => null) : null
    if (payload && 'ok' in payload) return payload as Connection
    throw new Error(payload?.error ?? error.message)
  }
  return data as Connection
}

const StoreStatusPanel: React.FC<{ shopId: string }> = ({ shopId }) => {
  const toast = useToast()
  const [status, setStatus] = useState<SyncStatus | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [conn, setConn] = useState<Connection | null>(null)
  const [checking, setChecking] = useState(false)
  const [replacing, setReplacing] = useState(false)
  const [token, setToken] = useState('')
  const [wooKey, setWooKey] = useState('')
  const [wooSecret, setWooSecret] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_merchant_sync_status', { p_merchant_id: shopId })
    if (error) { setLoadError(error.message); return }
    setLoadError(null)
    setStatus(data as SyncStatus)
  }, [shopId])

  useEffect(() => {
    load()
    const t = setInterval(load, 60000)
    return () => clearInterval(t)
  }, [load])

  const check = async () => {
    setChecking(true)
    try { setConn(await invokeConnection({ shop_id: shopId, action: 'check' })) }
    catch (e: any) { toast.error(e.message) }
    finally { setChecking(false) }
  }

  const replace = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const body = status?.platform === 'woocommerce'
        ? { shop_id: shopId, action: 'update', woocommerce_consumer_key: wooKey, woocommerce_consumer_secret: wooSecret }
        : { shop_id: shopId, action: 'update', shopify_access_token: token }
      const result = await invokeConnection(body)
      setConn(result)
      if (result.saved) {
        toast.success('Store connection replaced')
        setReplacing(false); setToken(''); setWooKey(''); setWooSecret('')
      }
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setSaving(false)
    }
  }

  if (loadError) return null // members without access, or the function not yet deployed: show nothing rather than an error
  if (!status) return <div className="glass-card rounded-2xl p-6 text-white/30 text-sm">Loading store status…</div>

  const nextIn = status.last_completed_at && status.typical_interval_minutes
    ? Math.max(0, status.typical_interval_minutes - Math.round((Date.now() - new Date(status.last_completed_at).getTime()) / 60000))
    : null

  return (
    <div className="glass-card rounded-2xl p-6 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading font-bold text-white text-lg">Store status</h3>
        {!status.sync_enabled && (
          <span className="text-xs rounded-full px-2.5 py-1 bg-yellow-500/10 text-yellow-300 border border-yellow-500/20">Sync paused</span>
        )}
      </div>

      {!status.sync_enabled && (
        <p className="text-sm text-white/60">
          Your store isn't syncing yet. <Link to="/onboarding" className="text-orange-400 hover:underline">Finish setup</Link> and press Go live, or ask the person who set up your store.
        </p>
      )}
      {!status.has_location && (
        <p className="text-sm text-red-300">Your store has no FLOWSTOCK stock location, so stock can't sync. Run setup again from <Link to="/onboarding" className="underline">Onboarding</Link>.</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat k="Last full sync"
          v={status.last_completed_at ? timeAgo(status.last_completed_at) : 'Not yet'}
          s={status.last_completed_at
            ? `${new Date(status.last_completed_at).toLocaleString()}${status.last_completed_products ? ` · ${status.last_completed_products.toLocaleString()} products checked` : ''}`
            : 'The first sync starts within a few minutes of going live'} />
        <Stat k={status.running ? 'Syncing now' : 'Next full sync'}
          v={status.running
            ? (status.running.total_batches ? `Batch ${(status.running.batch ?? 0) + 1} of ${status.running.total_batches}` : 'Starting')
            : nextIn === null ? '—' : nextIn === 0 ? 'Due now' : `in ~${nextIn} min`}
          s={status.typical_interval_minutes ? `Usually every ${status.typical_interval_minutes} min for your store` : 'Every 15–60 min, depending on catalog size'} />
        <Stat k="Single stock changes" v="~3½ min" s="Typical time for one change to reach your store. 90% arrive within 13 min." />
      </div>

      {status.last_failure && (
        <div className="p-3 rounded-lg border border-red-500/20 bg-red-500/5 text-sm">
          <p className="text-red-300">The last sync attempt failed {timeAgo(status.last_failure.at)}.</p>
          {status.last_failure.error && <p className="text-red-200/60 text-xs mt-1 break-words">{status.last_failure.error}</p>}
          <p className="text-white/40 text-xs mt-1">It retries automatically. If this stays here for more than an hour, contact support.</p>
        </div>
      )}

      <div className="border-t border-white/[0.06] pt-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-white/60">Store connection</span>
          {conn && (conn.ok
            ? <span className="text-xs text-green-400">Working{conn.missing_scopes?.length ? ', but missing permissions' : ''}</span>
            : <span className="text-xs text-red-300">Not working</span>)}
          <button type="button" onClick={check} disabled={checking} className="ml-auto px-3 py-1.5 rounded-full border border-white/15 text-white/70 hover:text-white text-xs disabled:opacity-50">
            {checking ? 'Checking…' : 'Check connection'}
          </button>
          {status.connection !== 'shopify_app' && (
            <button type="button" onClick={() => setReplacing((r) => !r)} className="px-3 py-1.5 rounded-full border border-orange-500/30 text-orange-300 hover:bg-orange-500/10 text-xs">
              {status.platform === 'woocommerce' ? 'Replace API key' : 'Replace token'}
            </button>
          )}
        </div>
        {status.connection === 'shopify_app' && (
          <p className="text-xs text-white/40">
            Connected through the FlowStock app in Shopify. If the connection stops working, open your Shopify admin,
            go to <b className="text-white/60">Apps → FlowStock</b> and approve it again. You'll come straight back here.
          </p>
        )}
        {conn && !conn.ok && conn.reason && <p className="text-sm text-red-200/80">{conn.reason}</p>}
        {conn?.missing_scopes && conn.missing_scopes.length > 0 && (
          <ul className="text-sm text-yellow-100/80 space-y-1">
            {conn.missing_scopes.map((m) => <li key={m.scope}><code className="text-xs bg-white/[0.06] px-1.5 py-0.5 rounded">{m.scope}</code> needed to {m.why}</li>)}
          </ul>
        )}
        {replacing && (
          <form onSubmit={replace} className="flex flex-col sm:flex-row gap-3">
            {status.platform === 'woocommerce' ? (
              <>
                <input aria-label="Consumer key" className="glass-input font-mono flex-1" placeholder="ck_…" value={wooKey} onChange={(e) => setWooKey(e.target.value)} autoComplete="off" />
                <input aria-label="Consumer secret" type="password" className="glass-input font-mono flex-1" placeholder="cs_…" value={wooSecret} onChange={(e) => setWooSecret(e.target.value)} autoComplete="off" />
              </>
            ) : (
              <input aria-label="New Admin API access token" type="password" className="glass-input font-mono flex-1" placeholder="shpat_…" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" />
            )}
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-full bg-orange-500 hover:bg-orange-400 text-black font-semibold text-sm disabled:opacity-50">
              {saving ? 'Testing…' : 'Test and save'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

const Stat: React.FC<{ k: string; v: string; s: string }> = ({ k, v, s }) => (
  <div className="min-w-0">
    <p className="text-xs text-white/40">{k}</p>
    <p className="font-heading font-semibold text-white text-xl">{v}</p>
    <p className="text-xs text-white/35 break-words">{s}</p>
  </div>
)

export default StoreStatusPanel
